import { queryClient } from './query-client';
import { forgetCoords } from './geolocation';
import { useAuthStore } from '../stores/auth.store';

/**
 * Every browser-side store that can identify or describe the user who just
 * signed out. Sign-out must leave a device with no trace of them: the next
 * person to sign in on a shared phone or a municipal counter tablet should
 * have no idea who was here before.
 *
 * Deliberately NOT cleared: `mycity.theme` and the i18n locale cache. Those
 * are device preferences that reveal nothing about identity, and wiping
 * them would reset the UI for the device owner on every sign-out.
 */
const USER_SCOPED_KEYS = [
  // Which municipality the user follows — identifying on its own.
  'mycity.tenantId',
  // Same, for the announcements feed's remembered city.
  'mycity-announce-tenant',
] as const;

/** Must match `cacheName` for the `/v1` NetworkFirst route in vite.config.ts. */
const API_CACHE_NAME = 'mycity-api-cache';

/**
 * Purge all user-scoped client state. Best-effort by design: sign-out must
 * complete even if a storage backend is unavailable (private mode, an
 * older browser, a revoked permission).
 */
export async function clearUserScopedState(): Promise<void> {
  // In-memory server cache (reports, notifications, profile, staff lists).
  try {
    queryClient.clear();
  } catch {
    /* nothing cached */
  }

  // Auth mirror — drop the profile immediately rather than waiting for the
  // onAuthStateChanged round trip.
  try {
    useAuthStore.getState().reset();
  } catch {
    /* store not initialised */
  }

  // Persisted, user-identifying preferences.
  try {
    for (const key of USER_SCOPED_KEYS) localStorage.removeItem(key);
  } catch {
    /* localStorage blocked */
  }

  // The service worker's cached API responses survive a reload, so without
  // this the next account could be served the previous user's data while
  // offline or on a flaky connection.
  try {
    await globalThis.caches?.delete(API_CACHE_NAME);
  } catch {
    /* Cache API unavailable */
  }
  // The remembered position is personal data too.
  forgetCoords();
}
